import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { UserPlus, Users } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FormField,
  PageHeader,
  SearchPanel,
  Select,
  StatusBadge,
  TextField,
  type Column,
} from '@/components';
import { ORGANIZATION_TYPES, type OrganizationType } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { formatDateTime } from '@/lib/format';
import { useUsers, type UserDto, type UserListFilter } from './api';
import { OrgBadge } from './OrgBadge';
import './admin-users.css';

const ACTIVE_OPTIONS = [
  { value: 'true', label: '有効' },
  { value: 'false', label: '無効' },
];

/** 検索条件は URL クエリに保持する（詳細から戻っても条件が残る） */
function useFilterFromUrl(): [UserListFilter, (f: UserListFilter) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<UserListFilter>(
    () => ({
      q: params.get('q') ?? '',
      organizationType: (params.get('organizationType') ?? '') as OrganizationType | '',
      isActive: (params.get('isActive') ?? '') as UserListFilter['isActive'],
    }),
    [params],
  );
  const setFilter = (f: UserListFilter) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, setFilter];
}

export function UserListPage() {
  const navigate = useNavigate();
  const { yearPath } = useFiscalYear();
  const [filter, setFilter] = useFilterFromUrl();
  const { data, isPending, isFetching, error, refetch } = useUsers(filter);
  const { register, handleSubmit, reset } = useForm<UserListFilter>({ values: filter });

  const columns: Column<UserDto>[] = [
    { key: 'loginId', header: 'ログインID', sortable: true, width: 180, render: (u) => <span className="bs-num">{u.loginId}</span> },
    { key: 'name', header: '氏名', sortable: true },
    { key: 'organizationType', header: '組織区分', sortable: true, width: 120, render: (u) => <OrgBadge type={u.organizationType} /> },
    { key: 'businessOperatorId', header: '事業実施者ID', numeric: true, sortable: true, width: 130 },
    {
      key: 'isActive',
      header: '状態',
      width: 90,
      align: 'center',
      sortable: true,
      render: (u) => (u.isActive ? <StatusBadge tone="success" dot>有効</StatusBadge> : <StatusBadge dot>無効</StatusBadge>),
    },
    {
      key: 'mfaEnabled',
      header: '2段階認証',
      width: 150,
      sortable: true,
      render: (u) => (
        <span className="bs-user-mfa">
          {u.mfaEnabled ? <StatusBadge tone="success" dot>有効</StatusBadge> : <StatusBadge dot>未設定</StatusBadge>}
          <span className={u.mfaRequired ? 'bs-user-mfa__req is-required' : 'bs-user-mfa__req'}>{u.mfaRequired ? '必須' : '任意'}</span>
        </span>
      ),
    },
    { key: 'lastLoginAt', header: '最終ログイン', sortable: true, width: 170, render: (u) => <span className="bs-num">{formatDateTime(u.lastLoginAt) || '—'}</span> },
  ];

  const summary = [filter.q && `キーワード: ${filter.q}`, filter.organizationType, filter.isActive && (filter.isActive === 'true' ? '有効' : '無効')]
    .filter(Boolean)
    .join(' ／ ');

  return (
    <>
      <PageHeader
        eyebrow="システム管理"
        title="ユーザー管理"
        description="ログインユーザーの登録・編集を行います。漁協ユーザーには事業実施者を紐づけてください。"
        actions={
          <Button variant="primary" icon={<UserPlus size={16} />} onClick={() => navigate(yearPath('admin/users/new'))}>
            新規登録
          </Button>
        }
      />

      <SearchPanel
        onSubmit={handleSubmit((v) => setFilter(v))}
        onReset={() => {
          reset({ q: '', organizationType: '', isActive: '' });
          setFilter({});
        }}
        summary={summary || '条件なし'}
        searching={isFetching}
        columns={4}
      >
        <FormField label="キーワード" span={2} hint="ログインID・氏名の部分一致">
          <TextField placeholder="例）suisan" {...register('q')} />
        </FormField>
        <FormField label="組織区分">
          <Select placeholder="すべて" options={ORGANIZATION_TYPES.map((t) => ({ value: t, label: t }))} {...register('organizationType')} />
        </FormField>
        <FormField label="状態">
          <Select placeholder="すべて" options={ACTIVE_OPTIONS} {...register('isActive')} />
        </FormField>
      </SearchPanel>

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="ユーザー一覧"
          columns={columns}
          rows={data ?? []}
          rowKey={(u) => u.id}
          loading={isPending}
          defaultSort={{ key: 'loginId', direction: 'asc' }}
          pagination={{ pageSize: 20 }}
          onRowClick={(u) => navigate(yearPath(`admin/users/${u.id}`))}
          rowClassName={(u) => (u.isActive ? undefined : 'is-muted')}
          empty={<EmptyState compact icon={<Users />} title="該当するユーザーはいません" description="検索条件を変更してください。" />}
        />
      )}
    </>
  );
}
